import { createApi } from "@reduxjs/toolkit/query/react";
import fetchBaseQueryCSRF from "../helper";

export type CampaignLeaksPlatform = 'google_ads' | 'bing_ads';
export type Granularity = 'weekly' | 'monthly' | 'quarterly' | 'yearly';
export type Comparison = 'previous_period' | 'none';

/** null = every region ("All"), campaigns without a region included. */
export type RegionsParam = string[] | null;

export type CampaignLeaksRegion = { region_name: string; ad_cost: number };

export type LeakedSearchTermRow = {
    id: string;
    campaign_type: string;
    region_name: string | null;
    account_name: string;
    campaign_name: string;
    ad_group_name: string;
    keyword: string | null;
    keyword_match_type: 'EXACT' | 'PHRASE' | 'BROAD' | null;
    search_term: string;
    ad_cost: number;
    ad_clicks: number;
};

export type LeakedSearchTermsResponse = {
    window: { start: string; end: string } | null;
    has_platform_data: boolean;
    kpis: {
        leaking_terms: number;
        ad_cost: number;
        leaked_spend: number;
        leaked_clicks: number;
        leaked_cpc: number | null;
    };
    rows: LeakedSearchTermRow[];
};

export type Sums = {
    ad_cost: number;
    ad_clicks: number;
    ad_impressions: number;
    ad_conversions: number;
    ad_conversions_value: number;
};

export type AccountPerformanceRow = {
    campaign_type: string;
    is_brand: boolean;
    current: Sums;
    compare: Sums | null;
};

export type AccountPerformanceResponse = {
    template: 'lead' | 'ecommerce';
    period: { start: string; end: string };
    compare: { start: string; end: string } | null;
    compare_coverage: number | null;
    rows: AccountPerformanceRow[];
    account_total: { current: Sums; compare: Sums | null };
};

const campaignLeaks = createApi({
    baseQuery: fetchBaseQueryCSRF({ baseUrl: process.env.CROSS_API_URL }),
    reducerPath: 'api.campaignLeaks',
    tagTypes: ['campaignLeaks'],
    endpoints: (builder) => ({
        getCampaignLeaksRegions: builder.query<{ regions: CampaignLeaksRegion[] }, { client: string; platform: CampaignLeaksPlatform }>({
            query: ({ client, platform }) => ({
                url: '/campaign-leaks/regions',
                method: 'POST',
                body: { client, platform },
            }),
        }),
        getLeakedSearchTerms: builder.query<LeakedSearchTermsResponse, {
            client: string;
            platform: CampaignLeaksPlatform;
            regions: RegionsParam;
        }>({
            query: ({ client, platform, regions }) => ({
                url: '/campaign-leaks/leaked-search-terms',
                method: 'POST',
                body: { client, platform, regions },
            }),
        }),
        getAccountPerformance: builder.query<AccountPerformanceResponse, {
            client: string;
            platform: CampaignLeaksPlatform;
            granularity: Granularity;
            period: string;
            comparison: Comparison;
            regions: RegionsParam;
        }>({
            query: ({ client, platform, granularity, period, comparison, regions }) => ({
                url: '/campaign-leaks/account-performance',
                method: 'POST',
                body: { client, platform, granularity, period, comparison, regions },
            }),
        }),
    }),
});

export const {
    useGetCampaignLeaksRegionsQuery,
    useGetLeakedSearchTermsQuery,
    useGetAccountPerformanceQuery,
} = campaignLeaks;

export default campaignLeaks;
